import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UsageBiasChart } from '../components/UsageBiasChart';
import { UsageBreakdownSection } from '../components/UsageBreakdownSection';
import { UsageKpiCards } from '../components/UsageKpiCards';
import {
  useUsageEvolutionQuery,
  useUsageKpisQuery,
  useUsageMonitoringFilters,
  type UsageDetailDrilldown,
} from '../hooks/useUsageMonitoring';
import type { UsageByGroupRow, UsageGroupBy } from '../types';

export function UsageMonitoringOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { queryParams, applyDetailDrilldown } = useUsageMonitoringFilters();

  const kpisQuery = useUsageKpisQuery(queryParams);
  const evolutionQuery = useUsageEvolutionQuery(queryParams);

  const goToDetail = useCallback(
    (opts: UsageDetailDrilldown) => {
      applyDetailDrilldown(opts);
      navigate('/usage-monitoring/detalle');
    },
    [applyDetailDrilldown, navigate],
  );

  const onGroupClick = useCallback(
    (row: UsageByGroupRow, groupBy: UsageGroupBy) => {
      if (groupBy === 'user') {
        goToDetail({
          userId: row.group_id,
          supplierId: null,
          status: '',
          presence: '',
          replaceScope: true,
        });
        return;
      }
      goToDetail({
        supplierId: row.group_id,
        userId: null,
        status: '',
        presence: '',
        replaceScope: true,
      });
    },
    [goToDetail],
  );

  const errorMessage =
    (kpisQuery.error as { message?: string } | null)?.message ||
    (evolutionQuery.error as { message?: string } | null)?.message;

  return (
    <div className="space-y-6">
      {errorMessage ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('adoption.loadError')}: {errorMessage}
        </div>
      ) : null}

      <UsageKpiCards
        kpis={kpisQuery.data}
        loading={kpisQuery.isLoading || kpisQuery.isFetching}
        onDrilldown={goToDetail}
      />

      <UsageBreakdownSection onGroupClick={onGroupClick} />

      <UsageBiasChart
        series={evolutionQuery.data?.series}
        loading={evolutionQuery.isLoading || evolutionQuery.isFetching}
      />
    </div>
  );
}
